import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header/Header";
import Button from "../components/Button/Button";
import '../css/Projets.css';
import { useTranslation } from 'react-i18next';
import { fetchProjects, fetchProjectCategories } from "../services/api";

const resolveIconPath = (icon) => (icon ? (icon.startsWith('/') ? icon : `/${icon}`) : null);

function Projets() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        const [projectsData, categoriesData] = await Promise.all([
          fetchProjects(),
          fetchProjectCategories()
        ]);
        if (mounted) {
          setProjects(projectsData || []);
          setCategories(categoriesData || []);
          if (categoriesData?.length) {
            const defaultCategory = categoriesData.find((cat) => cat.isDefault) || categoriesData[0];
            setActiveCategory(defaultCategory.id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des projets', error);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  const learnMoreLabel = useMemo(() => t('projects.learnMore'), [t]);
  const filteredProjects = activeCategory
    ? projects.filter((p) => p.category === activeCategory)
    : projects;

  return (
    <div>
      <Header title={t('projects.title')} />

      {categories.length > 1 && (
        <nav className="projets-tabs">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`projets-tab${activeCategory === cat.id ? ' projets-tab-active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label[lang] || cat.label.fr}
            </button>
          ))}
        </nav>
      )}

      {filteredProjects.length === 0 && (
        <p className="projets-empty">{t('projects.emptyCategory')}</p>
      )}

      {filteredProjects.map((projet, index) => (
        <motion.div 
          key={projet.id}
          initial={{ opacity: 0, y: 50 }} // Animation commence en bas
          whileInView={{ opacity: 1, y: 0 }} // Animation lorsqu'il est visible
          viewport={{ once: true }} // Ne s'anime qu'une fois
          transition={{ duration: 0.3, ease: "easeIn" }} // Animation fluide
          className="projet-wrapper"
        >
          {index % 2 !== 0 && <img src="assets/black-line-return.png" alt="background" className="black-line" />}
          
          <div className={index % 2 === 0 ? "projets-container" : "projets-container-black"}>
            <div className="projets-container-img">
              <img src={projet.logo} alt={projet.nom[lang]} className="bg-white" />
            </div>
            <div className="projets-container-details">
              <h1>{projet.nom[lang]}</h1>
              <p>{projet.contexte[lang]}</p>
              {projet.resume && projet.resume[lang] && <p>{projet.resume[lang]}</p>}

              <div className="projets-container-details-icons">
                {projet.technologies.map((tech) => (
                  <figure key={tech.id}>
                    {resolveIconPath(tech.icon) ? (
                      <img src={resolveIconPath(tech.icon)} alt={tech.label[lang] || tech.label.fr} />
                    ) : (
                      <span className="tech-placeholder">{tech.label[lang] || tech.label.fr}</span>
                    )}
                    <figcaption>{tech.label[lang] || tech.label.fr}</figcaption>
                  </figure>
                ))}
              </div>

              <Button url={`/projet/${projet.seturl}`} texte={learnMoreLabel} />
            </div>
          </div>

          {index % 2 !== 0 && <img src="assets/black-line.png" alt="background" className="black-line" />}
        </motion.div>
      ))}
    </div>
  );
}

export default Projets;